/*
 * <wrl-fit-finder> — the tractor fitment finder.
 *
 * Narrowing comes from wrl-fit-core.js, compiled from the portal's lib/fitment/core.ts, so this
 * file never reimplements the cascade — the dealer portal and the storefront give the same answer
 * for the same tractor because they run the same code.
 *
 * Three things this file is responsible for that the engine is not:
 *
 *  1. Lazy loading. The snapshot is ~47 KB gzipped and the homepage must not pay for it on every
 *     view, so nothing loads until the shopper looks at the widget or touches it.
 *  2. Live commerce data. The snapshot deliberately carries no price, image or stock status — only
 *     part numbers and handles. Everything a shopper sees about a product is fetched at render
 *     time from /products/{handle}.js, which is why a stale snapshot can never show a wrong price.
 *  3. Letting a shopper type. There are 991 tractor models; a dropdown that long is unusable, so
 *     model and loader are type-ahead comboboxes.
 */
(function () {
  "use strict";

  var NO_VARIANT = "__none__";
  var NO_STATION = "__none__";
  var MAX_MATCHES = 80;
  /* Below this many options the whole list opens on focus, because it is short enough to read as
     a genuine set of choices. Above it, the shopper has to type first: an arbitrary alphabetical
     slice of 991 tractor models reads as "these are your options" when it is nothing of the kind.
     In practice this means the model field always requires typing and the loader field - usually
     one to three options once a tractor is chosen - still opens on its own. */
  var OPEN_ON_FOCUS_MAX = 25;

  /* One cache per page load. A model's four product roles routinely repeat across configurations,
     and rear remotes are eight SKUs on one product — without this a single selection would refetch
     the same handle a dozen times. */
  var productCache = new Map();

  /* Selects the endpoint path and names the cache slot, and is what an incoming payload is checked
     against. The deployed engine's own constant is cross-checked in boot() when it exposes one, to
     catch theme JS and wrl-fit-core.js being shipped out of step. */
  /* A ceiling on local work, not on network traffic — wrl-fit-store.js holds a 30s per-key failure
     cooldown, so at most one request actually leaves the page in that window however often this
     retries (verified: 25 attempts, 0 requests). The cap can therefore be generous enough that a
     shopper who waits out a brief outage and taps again still recovers, rather than being stuck
     with the error card for the rest of the page load. */
  var MAX_BOOT_FAILURES = 8;

  var SNAPSHOT_VERSION = 1;
  var SNAPSHOT_KEY = "snapshot.v" + SNAPSHOT_VERSION;

  var scriptPromises = new Map();
  function loadScript(url) {
    if (scriptPromises.has(url)) return scriptPromises.get(url);
    var p = new Promise(function (resolve, reject) {
      var s = document.createElement("script");
      s.src = url;
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error("failed to load " + url)); };
      document.head.appendChild(s);
    }).catch(function (err) {
      /* Uncached on failure, matching wrl-fit-check.js. Keeping a rejected promise here made one
         flaky request permanent for every instance on the page — survivable when this only ever
         loaded a same-origin CDN asset, not survivable now that a retry is the recovery path. */
      scriptPromises.delete(url);
      throw err;
    });
    scriptPromises.set(url, p);
    return p;
  }

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c];
    });
  }

  function slug(s) {
    return String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  }

  function money(cents) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: currency }).format(cents / 100);
    } catch (e) {
      return "$" + (cents / 100).toFixed(2);
    }
  }

  /* Eight rear-remote part numbers stacked in a 240px column is noise, and it made that one card
     twice the height of its neighbours. The shopper chooses by spool count from the chips below;
     the full list belongs on the product page. */
  function skuLine(skus) {
    if (skus.length <= 2) return skus.join(" / ");
    return skus[0] + " +" + (skus.length - 1) + " more";
  }

  function rootPath() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root || "/";
  }

  /*
   * How a model or loader reads in the list.
   *
   * The catalogue's model names already lead with the manufacturer ("Massey Ferguson 1760M"), so
   * appending the make produces "Massey Ferguson 1760M - Massey Ferguson". The make is only worth
   * showing when it actually disambiguates, which is the loader case: Branson BL200 and TYM BL200
   * are different records with the same model number, and the key — not the name — tells them
   * apart. So append the make only when the name does not already carry it.
   */
  function optionLabel(o) {
    var name = o.name || "";
    var make = o.make || "";
    if (!make) return name;
    if (name.toLowerCase().indexOf(make.toLowerCase()) === 0) return name;
    return name + " — " + make;
  }

  /* Matches a catalogue part number against a live product's variants.
     Exact first, then the +HQFF-stripped base, then the product's first variant — the same order
     the snapshot builder joins in, so both directions agree. */
  function variantForSku(product, sku) {
    var want = String(sku || "").trim().toUpperCase();
    var cut = want.indexOf("+");
    var base = cut === -1 ? want : want.slice(0, cut);
    var variants = product.variants || [];
    var i, vs, vcut;

    for (i = 0; i < variants.length; i++) {
      if (String(variants[i].sku || "").trim().toUpperCase() === want) return variants[i];
    }
    for (i = 0; i < variants.length; i++) {
      vs = String(variants[i].sku || "").trim().toUpperCase();
      vcut = vs.indexOf("+");
      if (vcut !== -1 && vs.slice(0, vcut) === base) return variants[i];
    }
    return variants[0] || null;
  }

  function fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);
    var p = fetch(rootPath() + "products/" + handle + ".js")
      .then(function (res) {
        // A 404 means the handle was renamed or the product unpublished since the last snapshot.
        // That degrades to the "call to order" path and self-heals on the next publish.
        return res.ok ? res.json() : null;
      })
      .catch(function () { return null; });
    productCache.set(handle, p);
    return p;
  }

  /*
   * A type-ahead combobox over the model and loader lists.
   *
   * Hand-rolled rather than pulled from a library: the theme's CDN allowlist and the no-new-
   * dependency rule both argue against it, and the behaviour needed here is small. Matching is
   * token-based, so "mf 1760" and "1760m" both find "Massey Ferguson 1760M" — a shopper types the
   * number on the hood, not the catalogue's full string.
   */
  class Combo {
    constructor(root, onChange) {
      this.root = root;
      this.input = root.querySelector("input");
      this.list = root.querySelector("[role='listbox']");
      this.clearBtn = root.querySelector(".wrl-fit__clear");
      this.onChange = onChange;
      this.options = [];
      this.matches = [];
      this.active = -1;
      this.value = null;

      var self = this;

      this.input.addEventListener("focus", function () { self.open(""); });
      this.input.addEventListener("input", function () {
        self.open(self.input.value);
      });
      this.input.addEventListener("keydown", function (e) { self.onKey(e); });
      this.input.addEventListener("blur", function () {
        // Let a click on an option land before the list closes.
        window.setTimeout(function () { self.close(true); }, 150);
      });

      this.list.addEventListener("mousedown", function (e) {
        var li = e.target.closest("[data-key]");
        if (!li) return;
        e.preventDefault();
        self.select(li.getAttribute("data-key"));
      });

      this._raf = null;

      if (this.clearBtn) {
        this.clearBtn.addEventListener("click", function () {
          self.select(null);
          self.input.focus();
        });
      }
    }

    setOptions(options, current, placeholder) {
      this.options = options || [];
      this.value = current || null;
      this.input.placeholder = placeholder || "";
      this.input.disabled = this.options.length === 0;
      this.input.value = this.labelFor(this.value);
      if (this.clearBtn) this.clearBtn.hidden = !this.value;
    }

    labelFor(key) {
      if (!key) return "";
      for (var i = 0; i < this.options.length; i++) {
        if (this.options[i].key === key) return optionLabel(this.options[i]);
      }
      return "";
    }

    filter(query) {
      var tokens = String(query || "").toLowerCase().split(/\s+/).filter(Boolean);
      if (!tokens.length) return this.options.slice(0, MAX_MATCHES);
      var out = [];
      for (var i = 0; i < this.options.length && out.length < MAX_MATCHES; i++) {
        var hay = optionLabel(this.options[i]).toLowerCase();
        var ok = true;
        for (var t = 0; t < tokens.length; t++) {
          if (hay.indexOf(tokens[t]) === -1) { ok = false; break; }
        }
        if (ok) out.push(this.options[i]);
      }
      return out;
    }

    open(query) {
      if (this.input.disabled) return;

      // A shopper who focuses a filled field wants to change it, so treat the text already there
      // as "no query" rather than as a filter matching only itself.
      var q = query === this.labelFor(this.value) ? "" : query;

      if (!q.trim() && this.options.length > OPEN_ON_FOCUS_MAX) {
        this.close(false);
        return;
      }

      this.matches = this.filter(q);
      this.active = -1;

      if (!this.matches.length) {
        this.list.innerHTML = '<li class="wrl-fit__option-empty" role="presentation">No match</li>';
      } else {
        this.list.innerHTML = this.matches.map(function (o, i) {
          return '<li role="option" id="' + esc(this.list.id) + "-opt-" + i + '" data-key="' +
            esc(o.key) + '" aria-selected="' + (o.key === this.value) + '">' +
            esc(optionLabel(o)) + "</li>";
        }, this).join("");
      }

      this.list.hidden = false;
      this.input.setAttribute("aria-expanded", "true");
      this.positionList();
      this.track();
    }

    /*
     * Keep the fixed list glued to its input with rAF rather than scroll/resize listeners.
     *
     * Fixed coordinates are viewport coordinates, so they are wrong the instant the input moves.
     * Enumerating the ways it can move means catching scroll on the right ancestor (capture phase,
     * since scroll does not bubble), plus resize - and still missing the sticky header collapsing,
     * a late image reflowing the page, or a PageFly animation, none of which fire either event.
     * One rAF loop covers every cause without caring which it was. It runs only while the list is
     * open and only writes style when the rect actually changed, so the steady state is one cheap
     * read per frame. A hidden tab throttles rAF to nothing, which is harmless: the list is not on
     * screen, and the first frame after the tab is shown repositions it before paint.
     */
    track() {
      if (this._raf) return;
      var self = this;
      var last = "";
      var step = function () {
        // isConnected, not just hidden: a theme-editor section re-render or a PageFly content swap
        // drops the widget without ever closing the list, and the loop would then measure a
        // detached node every frame for the life of the page, holding the subtree in memory.
        if (self.list.hidden || !self.input.isConnected) { self._raf = null; return; }
        var r = self.input.getBoundingClientRect();
        var sig = r.top + ":" + r.left + ":" + r.width + ":" + window.innerHeight;
        if (sig !== last) {
          last = sig;
          self.positionList(r);
        }
        self._raf = window.requestAnimationFrame(step);
      };
      this._raf = window.requestAnimationFrame(step);
    }

    untrack() {
      if (this._raf) {
        window.cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    }

    /*
     * Pin the list to the input in viewport coordinates, and open upward when the space below is
     * too tight to be useful. Without the flip, a finder near the bottom of the window - which is
     * exactly where it sits on a short page - would drop its list off-screen instead of off-parent,
     * trading one invisible list for another.
     */
    positionList(rect) {
      if (this.list.hidden) return;

      var r = rect || this.input.getBoundingClientRect();
      var gap = 2;
      var margin = 8;
      var below = window.innerHeight - r.bottom - gap - margin;
      var above = r.top - gap - margin;
      var up = below < 160 && above > below;
      // No floor: a floor taller than the room available (short viewport, or a soft keyboard up)
      // pushes the far end of the list off-screen, which is the failure the flip exists to avoid.
      // A short list is still scrollable; an off-screen one is not reachable at all.
      var space = Math.max(0, Math.floor(up ? above : below));

      this.list.style.left = r.left + "px";
      this.list.style.width = r.width + "px";
      this.list.style.maxHeight = Math.min(280, space) + "px";

      if (up) {
        this.list.style.top = "auto";
        this.list.style.bottom = (window.innerHeight - r.top + gap) + "px";
      } else {
        this.list.style.bottom = "auto";
        this.list.style.top = (r.bottom + gap) + "px";
      }
    }

    close(restore) {
      this.untrack();
      this.list.hidden = true;
      this.input.setAttribute("aria-expanded", "false");
      this.input.removeAttribute("aria-activedescendant");
      this.active = -1;
      // Half-typed text is not a selection; put back whatever is actually selected.
      if (restore) this.input.value = this.labelFor(this.value);
    }

    setActive(i) {
      var items = this.list.querySelectorAll("[data-key]");
      if (!items.length) return;
      if (i < 0) i = items.length - 1;
      if (i >= items.length) i = 0;
      this.active = i;
      items.forEach(function (li, n) { li.classList.toggle("is-active", n === i); });
      items[i].scrollIntoView({ block: "nearest" });
      this.input.setAttribute("aria-activedescendant", items[i].id);
    }

    onKey(e) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        if (this.list.hidden) this.open(this.input.value);
        this.setActive(this.active + (e.key === "ArrowDown" ? 1 : -1));
      } else if (e.key === "Enter") {
        if (!this.list.hidden && this.active >= 0 && this.matches[this.active]) {
          e.preventDefault();
          this.select(this.matches[this.active].key);
        }
      } else if (e.key === "Escape") {
        this.close(true);
      }
    }

    select(key) {
      this.value = key;
      this.input.value = this.labelFor(key);
      if (this.clearBtn) this.clearBtn.hidden = !key;
      this.close(false);
      this.onChange(key);
    }
  }

  /* Inline rather than an asset: the badge is rendered at the moment a shopper resolves a fit, and
     a mark that arrives a beat after the words it belongs to reads as a page still loading - the
     opposite of the reassurance it exists to give. `currentColor` so it inherits the trust colour
     from CSS rather than pinning a hex here. */
  var CHECK_SVG =
    '<svg class="wrl-fit__check" viewBox="0 0 20 20" width="20" height="20" ' +
      'aria-hidden="true" focusable="false">' +
      '<circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" stroke-width="1.5"></circle>' +
      '<path d="M5.9 10.4l2.7 2.7 5.6-5.9" fill="none" stroke="currentColor" stroke-width="2" ' +
        'stroke-linecap="round" stroke-linejoin="round"></path>' +
    "</svg>";

  class WrlFitFinder extends HTMLElement {
    connectedCallback() {
      // A reconnect re-arms the trigger; only the one-time setup below is guarded.
      if (this._booted) {
        this.watch();
        return;
      }
      this._booted = true;

      this.mode = this.dataset.mode === "compact" ? "compact" : "full";
      this.deepLink = this.dataset.deepLink === "true";
      this.targetUrl = this.dataset.target || "/pages/find-your-fit";
      this.state = { modelKey: null, loaderKey: null, variant: null, station: null };

      /* Trust copy, all of it merchant-editable from the theme editor. Wording that makes a claim
         about the business ages faster than code does, and routing it through settings means it can
         be corrected without a theme push. Absent copy renders nothing rather than a default
         sentence, so clearing a field in the editor is a real way to turn a claim off. */
      /* Opt-in per placement, because it is a claim, not an ornament: it belongs beside a
         subheading that promises a verified fit, and reads as decoration beside one that just says
         which box to fill in first. */
      this.subCheck = this.dataset.subCheck === "true";
      this.showVerified = this.dataset.verified !== "false";
      this.verifiedLabel = this.dataset.verifiedLabel || "Verified fit";
      this.verifiedNote = this.dataset.verifiedNote || "";

      this.classList.add("wrl-fit");
      this.dataset.state = "idle";
      this.renderShell();

      /*
       * A visitor who followed a deep link has already told us their tractor - the selection is
       * sitting in the URL. Waiting for a scroll or a click before loading the snapshot would show
       * them an empty, disabled finder that silently already knows the answer. Lazy loading exists
       * for shoppers who have expressed no intent; this one has.
       */
      if (this.hasParams()) this.boot();
      else this.watch();
    }

    /*
     * True when the URL already carries a selection. Only `m` and `l` count: `v` and `s` narrow a
     * set they cannot produce on their own, so neither is a selection by itself.
     */
    hasParams() {
      // Only a finder that writes the URL should be made to read it. `m` and `l` are single common
      // letters - an unrelated `?l=en` on the homepage, where deep_link is off, would otherwise
      // force the 47 KB snapshot to load for a visitor who never asked for the finder.
      if (!this.deepLink) return false;
      var params = new URLSearchParams(window.location.search);
      return !!(params.get("m") || params.get("l"));
    }

    /*
     * Load on first sight or first touch.
     *
     * Re-observing on every connect matters: disconnectedCallback drops the observer, and a theme
     * that moves a section in the DOM would otherwise leave the widget with no trigger at all and
     * nothing would ever load.
     */
    /* `interactionOnly` arms the pointer and focus listeners but not the IntersectionObserver.
       Retrying after a failure MUST use it: an observer re-armed on an element that is already in
       view fires immediately, so a failing boot would re-enter itself as fast as the network
       allows. That is a loop that hammers the portal from every open tab. A human tapping the
       widget is a retry signal; being visible is not. */
    watch(interactionOnly) {
      if (this._loading) return;
      this.unwatch();

      var self = this;
      this._bootOnce = function () { self.boot(); };

      this.addEventListener("pointerdown", this._bootOnce);
      this.addEventListener("focusin", this._bootOnce);

      if (interactionOnly) return;

      if ("IntersectionObserver" in window) {
        this._io = new IntersectionObserver(function (entries) {
          if (entries.some(function (e) { return e.isIntersecting; })) self.boot();
        }, { rootMargin: "200px" });
        this._io.observe(this);
      } else {
        this.boot();
      }
    }

    unwatch() {
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
      if (this._bootOnce) {
        this.removeEventListener("pointerdown", this._bootOnce);
        this.removeEventListener("focusin", this._bootOnce);
        this._bootOnce = null;
      }
    }

    disconnectedCallback() {
      this.unwatch();
    }

    renderShell() {
      this.innerHTML =
        '<div class="wrl-fit__head">' +
          '<p class="wrl-fit__title">' + esc(this.dataset.heading || "Find what fits your tractor") + "</p>" +
          '<p class="wrl-fit__sub">' + (this.subCheck ? CHECK_SVG : "") + "<span>" +
            esc(this.dataset.subheading || "Pick your model and loader to see the verified kits that fit your machine.") +
          "</span></p>" +
        "</div>" +
        '<div class="wrl-fit__steps">' +
          this.comboHtml("model", "Tractor model") +
          this.comboHtml("loader", "Loader") +
          this.selectHtml("variant", "Variant") +
          this.selectHtml("station", "Operator station") +
        "</div>" +
        '<div class="wrl-fit__actions"></div>' +
        '<div class="wrl-fit__results"></div>';

      this.els = {
        steps: this.querySelector(".wrl-fit__steps"),
        actions: this.querySelector(".wrl-fit__actions"),
        results: this.querySelector(".wrl-fit__results"),
      };
    }

    stepId(name) {
      return "wrl-fit-" + name + "-" + Math.random().toString(36).slice(2, 8);
    }

    comboHtml(name, label) {
      var id = this.stepId(name);
      return '<div class="wrl-fit__step" data-step="' + name + '">' +
        '<label class="wrl-fit__label" for="' + id + '">' + esc(label) + "</label>" +
        '<div class="wrl-fit__combo" data-combo="' + name + '">' +
          '<input class="wrl-fit__input" id="' + id + '" type="text" role="combobox" ' +
            'autocomplete="off" spellcheck="false" aria-expanded="false" aria-autocomplete="list" ' +
            'aria-controls="' + id + '-list" placeholder="Loading&hellip;" disabled>' +
          '<button type="button" class="wrl-fit__clear" aria-label="Clear ' + esc(label) + '" hidden>&times;</button>' +
          '<ul class="wrl-fit__list" id="' + id + '-list" role="listbox" hidden></ul>' +
        "</div></div>";
    }

    selectHtml(name, label) {
      var id = this.stepId(name);
      return '<div class="wrl-fit__step" data-step="' + name + '" hidden>' +
        '<label class="wrl-fit__label" for="' + id + '">' + esc(label) + "</label>" +
        '<select class="wrl-fit__select" id="' + id + '" data-select="' + name + '"></select></div>';
    }

    boot() {
      if (this._loading) return;
      this._loading = true;
      this.unwatch();

      var self = this;

      var url = this.dataset.snapshotUrl;
      var snapshot = Promise.resolve().then(function () {
        /* Not assumed to exist. Document-order `defer` guarantees it on a normally rendered page,
           but a dynamically injected section (theme editor, Section Rendering API) ignores `defer`
           and runs scripts in load order — and a deep-linked finder boots synchronously from
           connectedCallback, so a TypeError here would escape this chain entirely and leave the
           comboboxes disabled at "Loading..." with no error card. */
        if (!window.WRLFitStore) throw new Error("wrl-fit-store.js has not loaded");

        /* Only this widget's own key family. Passing a keep-list that named the PDP key made the
           finder the authority on a version constant it does not own: bump the PDP schema in
           wrl-fit-check.js alone and every page carrying both widgets would delete the PDP cache on
           each boot, refetching ~73 KB forever with nothing to show the mistake. */
        window.WRLFitStore.evictOtherVersions("snapshot", SNAPSHOT_KEY);

        return window.WRLFitStore.load({
        key: SNAPSHOT_KEY,
        url: url,
        version: SNAPSHOT_VERSION,
        /* A background revalidation found different data. Redraw only if the shopper has not
           started narrowing: pulling results out from under someone mid-selection to deliver a
           renamed handle is worse than the stale handle, which already degrades to the call-us
           path when the product fetch 404s. */
        onRefresh: function (payload) {
          if (self.dataset.state !== "ready") return;
          if (self.state && (self.state.modelKey || self.state.loaderKey)) return;
          self.snapshot = payload;
          self.ix = self.core.indexSnapshot(payload);
          self.render();
        },
        });
      });

      Promise.all([snapshot, loadScript(this.dataset.coreUrl)])
        .then(function (results) {
          var payload = results[0].payload;
          if (!payload || !window.WRLFitCore) throw new Error("fitment assets did not initialise");

          /* asset_url used to bind snapshot to engine: one theme commit moved both fingerprints
             together. The snapshot now comes from a stable URL, so two things replace that. The
             first is SNAPSHOT_VERSION, which selects the endpoint path and the cache key and is
             therefore what the payload is checked against - wrl-fit-store.js has already rejected
             anything else before this point.

             The second is this: when the deployed engine publishes its own constant and the two
             disagree, the theme's JS and wrl-fit-core.js were shipped out of step, which is a
             deploy mistake worth failing loudly on. Guarded on `!== undefined` deliberately - an
             engine built before that re-export simply does not have it, and treating that as a
             version mismatch fails every load for every visitor. */
          var engineVersion = window.WRLFitCore.FIT_SNAPSHOT_VERSION;
          if (engineVersion !== undefined && engineVersion !== SNAPSHOT_VERSION) {
            throw new Error(
              "fitment engine expects snapshot v" + engineVersion + ", theme asks for v" + SNAPSHOT_VERSION
            );
          }

          self.snapshot = payload;
          /* Kept for debugging and for anything on the page that reaches for the old global.
             Nothing in this file reads it. */
          window.__WRL_FIT__ = payload;
          self.core = window.WRLFitCore;
          self.ix = self.core.indexSnapshot(self.snapshot);
          self.dataset.state = "ready";

          self.readParams();
          self.bindControls();
          self.render();
        })
        .catch(function (err) {
          self.dataset.state = "error";
          // Otherwise both comboboxes sit disabled reading "Loading..." forever.
          self.els.steps.querySelectorAll(".wrl-fit__input").forEach(function (input) {
            input.placeholder = "Unavailable";
          });
          self.els.results.innerHTML =
            '<p class="wrl-fit__empty">We could not load the tractor list just now. Call us on ' +
            esc(self.dataset.phone || "252-823-4570") + " and we will find your fit.</p>";
          if (window.console) console.error("[wrl-fit]", err);

          /* Retryable, but only on a deliberate interaction and only a few times. The data is now
             a cross-origin fetch, so a failure is likelier to be transient than it was for a CDN
             asset and a dead-until-reload widget would be wrong. Re-arming the observer here
             instead would loop: see `watch`. */
          self._loading = false;
          self._failures = (self._failures || 0) + 1;
          if (self._failures < MAX_BOOT_FAILURES) self.watch(true);
        });
    }

    /* Deep links carry slug + key so they read as English but resolve on the stable key:
       ?m=kubota-l3901--m_9f2c1a0b4d7e */
    readParams() {
      var params = new URLSearchParams(window.location.search);
      var keyOf = function (raw) {
        if (!raw) return null;
        var i = raw.lastIndexOf("--");
        return i === -1 ? raw : raw.slice(i + 2);
      };
      var m = keyOf(params.get("m"));
      var l = keyOf(params.get("l"));
      if (m && this.ix.modelByKey.has(m)) this.state.modelKey = m;
      if (l && this.ix.loaderByKey.has(l)) this.state.loaderKey = l;
      if (params.get("v")) this.state.variant = params.get("v");
      if (params.get("s")) this.state.station = params.get("s");
    }

    /*
     * Hand the shopper's tractor to the product pages.
     *
     * Written on the model, not on a resolved configuration: a shopper who names their tractor and
     * then wanders into the catalogue should still see the product pages talking about that
     * tractor, whether or not they finished narrowing here. The loader rides along for the
     * finder's own benefit on a return visit; the product page cannot use it, because the PDP
     * index is keyed by model only.
     *
     * Best-effort by design. Storage can be unavailable or full, and the badge this feeds is an
     * enhancement - never let it break a render.
     */
    rememberTractor() {
      if (!window.WRLFitStore || !window.WRLFitStore.saveTractor) return;
      var model = this.state.modelKey ? this.ix.modelByKey.get(this.state.modelKey) : null;
      if (!model) return;
      var loader = this.state.loaderKey ? this.ix.loaderByKey.get(this.state.loaderKey) : null;

      /* render() runs on every narrowing step, including the ones that change neither of these.
         localStorage writes are synchronous, so skipping the no-op ones keeps variant and operator
         station toggling off the main thread's critical path. */
      var signature = model.key + "|" + (loader ? loader.key : "");
      if (signature === this._rememberedTractor) return;
      this._rememberedTractor = signature;

      try {
        window.WRLFitStore.saveTractor({
          modelKey: model.key,
          modelName: model.name,
          loaderKey: loader ? loader.key : null,
          loaderName: loader ? loader.name : null,
        });
      } catch (e) {
        /* nothing to do */
      }
    }

    writeParams() {
      if (!this.deepLink) return;
      var params = new URLSearchParams(window.location.search);
      var set = function (key, value) {
        if (value) params.set(key, value);
        else params.delete(key);
      };
      var model = this.state.modelKey ? this.ix.modelByKey.get(this.state.modelKey) : null;
      var loader = this.state.loaderKey ? this.ix.loaderByKey.get(this.state.loaderKey) : null;
      set("m", model ? slug(model.name) + "--" + model.key : null);
      set("l", loader ? slug(loader.name) + "--" + loader.key : null);
      set("v", this.state.variant);
      set("s", this.state.station);
      var qs = params.toString();
      // Keep the fragment: a shopper who arrived on an in-page anchor should still be there after
      // changing a loader.
      window.history.replaceState({}, "",
        window.location.pathname + (qs ? "?" + qs : "") + window.location.hash);
    }

    targetHref() {
      var params = new URLSearchParams();
      var model = this.state.modelKey ? this.ix.modelByKey.get(this.state.modelKey) : null;
      var loader = this.state.loaderKey ? this.ix.loaderByKey.get(this.state.loaderKey) : null;
      if (model) params.set("m", slug(model.name) + "--" + model.key);
      if (loader) params.set("l", slug(loader.name) + "--" + loader.key);
      if (this.state.variant) params.set("v", this.state.variant);
      if (this.state.station) params.set("s", this.state.station);
      var qs = params.toString();
      return this.targetUrl + (qs ? "?" + qs : "");
    }

    bindControls() {
      var self = this;

      this.combos = {};
      ["model", "loader"].forEach(function (name) {
        var root = self.els.steps.querySelector('[data-combo="' + name + '"]');
        var key = name === "model" ? "modelKey" : "loaderKey";
        self.combos[name] = new Combo(root, function (value) {
          self.state[key] = value || null;
          self.render();
        });
      });

      this.querySelectorAll("[data-select]").forEach(function (select) {
        select.addEventListener("change", function (e) {
          self.state[e.target.getAttribute("data-select")] = e.target.value || null;
          self.render();
        });
      });
    }

    label(value) {
      if (value === NO_VARIANT) return "No variant";
      if (value === NO_STATION) return "Not specified";
      return value;
    }

    fillSelect(name, options, current) {
      var self = this;
      var step = this.els.steps.querySelector('[data-step="' + name + '"]');
      var select = step.querySelector("select");
      // Without a placeholder, an unanswered step sets value="" against options that have no ""
      // entry, so selectedIndex lands on -1 and the control renders blank. core.narrow only
      // auto-selects when a step has exactly one option, so this state is reachable.
      var html = current ? "" : '<option value="" disabled selected>Choose an option</option>';
      select.innerHTML = html + options.map(function (o) {
        return '<option value="' + esc(o) + '">' + esc(self.label(o)) + "</option>";
      }).join("");
      select.value = current || "";
      select.disabled = options.length <= 1;
      return step;
    }

    render() {
      // Model first, always: the shopper names their tractor, then the loaders that pair with it.
      var out = this.core.narrow(this.ix, this.state);
      this.state = out.selection;

      this.combos.model.setOptions(out.models, this.state.modelKey, "Type your tractor, e.g. L3901");
      this.combos.loader.setOptions(out.loaders, this.state.loaderKey,
        out.loaders.length ? "Type your loader, e.g. LA525" : "Choose a tractor first");

      var variantStep = this.fillSelect("variant", out.variants, this.state.variant);
      variantStep.hidden = !out.showVariant;

      var stationStep = this.fillSelect("station", out.stations, this.state.station);
      stationStep.hidden = !out.showStation;

      this.writeParams();
      this.rememberTractor();
      this.renderActions(out);

      if (this.mode === "compact") {
        this.els.results.innerHTML = "";
        return;
      }
      this.renderResults(out);
    }

    renderActions(out) {
      if (this.mode !== "compact") {
        this.els.actions.innerHTML = "";
        return;
      }
      // A real <button disabled> rather than an <a aria-disabled>: the anchor still navigated on
      // a mouse click (to the results page with no selection) while tabindex="-1" hid it from
      // keyboard users, and the [disabled] style never matched it.
      this.els.actions.innerHTML = out.result
        ? '<a class="wrl-fit__button" href="' + esc(this.targetHref()) + '">See what fits</a>'
        : '<button type="button" class="wrl-fit__button" disabled>See what fits</button>';
    }

    renderResults(out) {
      var r = out.result;
      if (!r) {
        if (out.ambiguous) {
          // More than one configuration matches and there is no step left to change. Telling the
          // shopper to keep narrowing would be a dead end - there is nothing left to touch.
          var phone = (this.snapshot.support || {}).phone || "";
          this.els.results.innerHTML =
            '<p class="wrl-fit__empty">More than one kit matches this tractor. Call us on ' +
            '<a href="tel:' + esc(phone.replace(/[^0-9+]/g, "")) + '">' + esc(phone) +
            "</a> and we will confirm the right one.</p>";
          return;
        }
        /*
         * Name the control that will actually narrow this, rather than counting configurations at
         * the shopper. "2 combinations still match" is a fact about our data; "choose your loader"
         * is the thing they can do about it, and it points at a control already on screen.
         *
         * Each branch can trust that an unanswered step is a real choice: narrow() auto-advances
         * any step with a single option, so a step still open here always has at least two. The
         * wording matches the step's own label, so the sentence and the control agree.
         */
        var next = null;
        if (!this.state.modelKey && out.models.length > 1) next = "your tractor model";
        else if (!this.state.loaderKey && out.loaders.length > 1) next = "your loader";
        else if (out.showVariant && !this.state.variant && out.variants.length > 1) next = "a variant";
        else if (out.showStation && !this.state.station && out.stations.length > 1) next = "your operator station";

        this.els.results.innerHTML = this.state.modelKey || this.state.loaderKey
          ? '<p class="wrl-fit__empty">Keep going - ' +
            (next ? "choose " + next + "." : "finish your selection above.") + "</p>"
          : "";
        return;
      }

      var title = r.model.name + (r.loader ? " with a " + r.loader.name + " loader" : "");
      var html = this.verifiedHtml(title);

      // Always rendered when present. These are constraints on the fit, not decoration.
      if (r.installWarning) {
        html += '<p class="wrl-fit__note wrl-fit__note--warn"><b>Before you install</b>' +
          esc(r.installWarning) + "</p>";
      }
      if (r.kitNotes) {
        html += '<p class="wrl-fit__note"><b>Please note</b>' + esc(r.kitNotes) + "</p>";
      }

      /*
       * One row of cards, not four stacked sections.
       *
       * A configuration usually resolves to four or five products across the four roles. Giving
       * each role its own grid meant each one was a single full-width card and the shopper had to
       * scroll past a hero-sized image to see the next part. Flattening puts the whole kit in one
       * horizontal run, with the role named on the card instead of in a heading above it.
       */
      var cards = []
        .concat(this.cardsFor("Valve kit", r.valveKit ? [r.valveKit] : [], false))
        .concat(this.cardsFor("Top link", r.topLinks, false))
        .concat(this.cardsFor("Side link", r.sideLinks, false))
        .concat(this.cardsFor("Rear remotes", r.rearRemotes, true));

      if (cards.length) {
        html += '<div class="wrl-fit__cards">' + cards.join("") + "</div>";
      }

      // An empty product group is not "unavailable" - plenty of kits are built to order - so this
      // says so in plain words rather than leaving a gap the shopper has to interpret.
      if (!r.valveKit) {
        html += '<p class="wrl-fit__empty">We do not list a valve kit for this combination - ' +
          "call us and we will check what fits.</p>";
      }

      this.els.results.innerHTML = html;
      this.hydrate();
    }

    /*
     * The badge.
     *
     * Rendered only from inside renderResults, and only once `narrow` has returned a single
     * resolved configuration - never as page furniture above an empty form. That is the whole
     * point: a badge sitting on the page before anyone has typed anything is a slogan, while the
     * same badge under a named tractor is a statement about the shopper's own machine that they
     * can check. It repeats the model and loader for exactly that reason.
     *
     * With the badge turned off in the editor the summary line renders as it always did, so
     * switching it off removes a claim rather than removing the shopper's context.
     */
    verifiedHtml(title) {
      if (!this.showVerified) {
        return '<p class="wrl-fit__summary">' + esc(title) + "</p>";
      }
      return '<div class="wrl-fit__verified">' +
        CHECK_SVG +
        '<div class="wrl-fit__verified-body">' +
          '<p class="wrl-fit__verified-label">' + esc(this.verifiedLabel) + "</p>" +
          '<p class="wrl-fit__summary">' + esc(title) + "</p>" +
          (this.verifiedNote
            ? '<p class="wrl-fit__verified-note">' + esc(this.verifiedNote) + "</p>"
            : "") +
        "</div></div>";
    }

    /* Returns card HTML for one role. Rear remotes collapse: up to eight catalogue SKUs are eight
       variants of one Shopify product, so they become one card with the spool counts inside. */
    cardsFor(role, parts, collapse) {
      if (!parts.length) return [];

      var groups = [];
      if (collapse) {
        var seen = {};
        parts.forEach(function (p) {
          var key = p.handle || "sku:" + p.sku;
          if (!seen[key]) {
            seen[key] = { handle: p.handle, skus: [] };
            groups.push(seen[key]);
          }
          seen[key].skus.push(p.sku);
        });
      } else {
        groups = parts.map(function (p) { return { handle: p.handle, skus: [p.sku] }; });
      }

      var self = this;
      return groups.map(function (g) {
        if (!g.handle) return self.callCardHtml(g.skus, role);
        return '<div class="wrl-fit__card" data-handle="' + esc(g.handle) + '" data-skus="' +
          esc(g.skus.join(",")) + '" data-role="' + esc(role) + '">' +
          '<div class="wrl-fit__card-body">' +
            '<p class="wrl-fit__role">' + esc(role) + "</p>" +
            '<p class="wrl-fit__loading">Loading&hellip;</p>' +
          "</div></div>";
      });
    }

    /* The other "call us" reason, and it must read differently: this part exists, we make it, it
       just has no storefront product. Never drop the row - that would make the configuration look
       as though it has no valve kit at all. */
    callCardHtml(skus, role) {
      // Phone only. The snapshot still carries support.email — it is part of the published
      // contact block — but this card asks for one action, and a sales mailbox is a slower path
      // than a call for someone standing in front of a tractor.
      var phone = (this.snapshot.support || {}).phone || "";
      return '<div class="wrl-fit__card"><div class="wrl-fit__card-body">' +
        (role ? '<p class="wrl-fit__role">' + esc(role) + "</p>" : "") +
        '<p class="wrl-fit__card-title">' + esc(skus[0]) + "</p>" +
        '<p class="wrl-fit__callout">Call to order - ' +
        '<a href="tel:' + esc(phone.replace(/[^0-9+]/g, "")) + '">' + esc(phone) + "</a>" +
        "</p></div></div>";
    }

    /* Live commerce data, fetched only now - after a tractor is picked, never on page load. */
    hydrate() {
      var self = this;
      this.els.results.querySelectorAll("[data-handle]").forEach(function (card) {
        var handle = card.getAttribute("data-handle");
        var skus = card.getAttribute("data-skus").split(",");
        var role = card.getAttribute("data-role") || "";

        fetchProduct(handle).then(function (product) {
          if (!product) {
            // Renamed or unpublished since the snapshot: degrade to the call path, never blank.
            card.outerHTML = self.callCardHtml(skus, role);
            return;
          }

          // Deduplicate by variant id. variantForSku falls back to the product's first variant,
          // so if the live SKUs have drifted from the catalogue's, every SKU in a collapsed
          // rear-remote group resolves to the same variant - which would render eight identical
          // chips, all linking to the same ?variant=, presented as distinct spool options.
          var variants = [];
          var seenVariantIds = {};
          skus.forEach(function (sku) {
            var v = variantForSku(product, sku);
            if (v && !seenVariantIds[v.id]) {
              seenVariantIds[v.id] = true;
              variants.push(v);
            }
          });
          var primary = variants[0] || (product.variants || [])[0];
          var url = rootPath() + "products/" + handle;
          // 520 of 818 products are multi-variant, and product-form honours ?variant= on load.
          var multi = (product.variants || []).length > 1;
          var href = primary && multi ? url + "?variant=" + primary.id : url;

          var media = product.featured_image
            ? '<a class="wrl-fit__card-media" href="' + esc(href) + '">' +
              '<img src="' + esc(product.featured_image) + '" alt="" loading="lazy" width="300" height="300"></a>'
            : "";

          var priceHtml;
          if (primary && primary.available) {
            priceHtml = '<p class="wrl-fit__price">' + esc(money(primary.price)) + "</p>";
          } else if (primary) {
            priceHtml = '<p class="wrl-fit__price wrl-fit__price--sold">' + esc(money(primary.price)) +
              " - out of stock</p>";
          } else {
            priceHtml = '<p class="wrl-fit__price wrl-fit__price--sold">Call for availability</p>';
          }

          var options = "";
          if (variants.length > 1) {
            options = '<div class="wrl-fit__options">' + variants.map(function (v) {
              return '<a class="wrl-fit__option" href="' + esc(url + "?variant=" + v.id) + '">' +
                esc(v.title) + "</a>";
            }).join("") + "</div>";
          }

          card.innerHTML = media +
            '<div class="wrl-fit__card-body">' +
              (role ? '<p class="wrl-fit__role">' + esc(role) + "</p>" : "") +
              '<p class="wrl-fit__card-title"><a href="' + esc(href) + '">' + esc(product.title) + "</a></p>" +
              '<p class="wrl-fit__sku">' + esc(skuLine(skus)) + "</p>" +
              priceHtml + options +
            "</div>";
        });
      });
    }
  }

  if (!window.customElements.get("wrl-fit-finder")) {
    window.customElements.define("wrl-fit-finder", WrlFitFinder);
  }
})();
